// Lead Nurture: notes while calling, "Just called", search across gyms, "Missing a lead? search GoHighLevel" and the
// intake diagnostics (routes/nurture_tools.py). Lead Nurture head's feedback, 2026-10-19.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, StickyNote, RotateCcw, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  getNurtureLeadNotes, searchNurtureLeads, nurtureGhlLookup, nurtureGhlAdd, getNurtureIntakeStatus,
} from "@/lib/api";

const err = (e, fallback) => e?.response?.data?.detail || fallback;
const shortGym = (name) => (name || "").split(" - ").pop();

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m >= 0 && m < 60) return m < 1 ? "just now" : `${m} min ago`;
  return d.toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

// The lead's history: GoHighLevel notes (previous follow-ups, e.g. "call after 6 pm") + FPA's calls, newest first.
export function LeadNotes({ leadId, version = 0, prominent = false, max = 8 }) {
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    if (!leadId) return undefined;
    let alive = true;
    setData(null);
    getNurtureLeadNotes(leadId, version > 0).then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setData({ notes: [], ghl_error: err(e, "Couldn't load notes") }); });
    return () => { alive = false; };
  }, [leadId, version]);
  const box = prominent ? "rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-3 py-2" : "rounded-md border border-[#222] bg-[#0a0a0a] px-2 py-1.5";
  if (!data) return <div className={`${box} text-[11px] text-zinc-500`} data-testid="lead-notes">Loading notes…</div>;
  const rows = all ? data.notes : data.notes.slice(0, max);
  return (
    <div className={`${box} space-y-1.5`} data-testid="lead-notes">
      {prominent && (
        <div className="text-[10px] uppercase tracking-wider text-amber-300/90 flex items-center gap-1">
          <StickyNote className="h-3 w-3" /> Notes &amp; previous calls ({data.notes.length})
        </div>
      )}
      {data.ghl_error && <div className="text-[10px] text-amber-400/80">{data.ghl_error}</div>}
      {data.notes.length === 0 && <div className="text-[11px] text-zinc-500">No notes yet in GoHighLevel or FPA.</div>}
      {rows.map((n) => (
        <div key={`${n.source}-${n.id}`} className="text-[11px] leading-snug" data-testid={`note-${n.source}`}>
          <div className="text-zinc-500">
            <span className={`mr-1 rounded px-1 text-[9px] font-semibold ${n.source === "ghl" ? "bg-sky-500/15 text-sky-300" : n.source === "agent" ? "bg-violet-500/15 text-violet-300" : "bg-rose-500/15 text-rose-300"}`}>
              {n.source === "ghl" ? "GHL" : n.source === "agent" ? "AUTO" : "FPA"}
            </span>
            {n.author || "—"} · {when(n.at)}
            {(n.source === "fpa" || n.source === "agent") && n.label ? <span className="text-zinc-300"> · {n.label}</span> : null}
            {n.source === "fpa" && n.callback_at && n.outcome === "callback" ? <span className="text-sky-300"> · callback {when(n.callback_at)}</span> : null}
          </div>
          {n.body && <div className={`whitespace-pre-line ${prominent ? "text-zinc-100 text-xs" : "text-zinc-300"}`}>{n.body}</div>}
        </div>
      ))}
      {data.notes.length > max && (
        <button className="text-[10px] text-zinc-400 underline" onClick={() => setAll((v) => !v)}>
          {all ? "Show fewer" : `Show all ${data.notes.length}`}
        </button>
      )}
    </div>
  );
}

const OUTCOME_SHORT = { booked: "Booked", callback: "Call back", no_answer: "No answer", not_interested: "Not interested",
  wrong_number: "Wrong number", confirmed: "Confirmed", reschedule: "Reschedule", cancelling: "Cancelling" };

// The rep's own calls of the last 2 hours: the lead is one tap away for a second call or a callback, wherever it
// moved on the board after the call.
export function JustCalled({ items, onCallAgain, onOutcome, onCallback }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-1.5" data-testid="just-called">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Just called (last 2 h)</div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {items.map(({ call, lead }) => {
          const st = call.outcome ? OUTCOME_SHORT[call.outcome] || call.outcome
            : call.talk_seconds > 0 ? "Talked" : call.mode === "exotel" && call.status && call.status !== "dialling" ? "No answer (Exotel)" : "No outcome yet";
          return (
            <div key={call.id} className="shrink-0 w-[230px] rounded-lg border border-[#262626] bg-[#0d0d0d] px-2.5 py-2" data-testid={`just-called-${lead.id}`}>
              <div className="text-sm font-medium truncate">{lead.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{shortGym(lead.gym)} · {when(call.created_at)} · <span className={call.outcome ? "text-zinc-300" : "text-amber-300"}>{st}</span></div>
              <div className="mt-1.5 flex gap-1">
                <Button size="sm" className="h-6 px-2 text-[11px]" disabled={!lead.phone} onClick={() => onCallAgain(lead, call.phone_id)} data-testid={`just-called-again-${lead.id}`}>
                  <RotateCcw className="h-3 w-3 mr-1" /> Call again
                </Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => onCallback(call, lead)}>Callback</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => onOutcome(call, lead)}>Outcome</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Search FPA's leads by name / phone across every gym; "Missing a lead?" looks it up in that gym's GoHighLevel.
export function LeadSearch({ gyms, renderLead, onAdded }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [lookup, setLookup] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      searchNurtureLeads(term).then((d) => { if (alive) setRes(d.leads); }).catch(() => { if (alive) setRes([]); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads by name or phone (all gyms)"
            className="h-8 pl-8 text-sm" data-testid="lead-search" />
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setLookup(true)} data-testid="ghl-lookup-btn">
          Missing a lead? Search GoHighLevel
        </Button>
      </div>
      {res && (
        <div className="card-surface p-2 space-y-2" data-testid="lead-search-results">
          <div className="flex items-center justify-between text-[11px] text-zinc-500 px-1">
            <span>{res.length ? `${res.length} found` : "Not on FPA's board."}</span>
            <button className="underline" onClick={() => setQ("")}>Clear</button>
          </div>
          {res.length === 0 && (
            <button className="text-xs text-sky-300 underline px-1" onClick={() => setLookup(true)}>Look “{q.trim()}” up in GoHighLevel</button>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {res.map((l) => <div key={l.id}>{renderLead(l)}</div>)}
          </div>
        </div>
      )}
      <GhlLookupDialog open={lookup} onOpenChange={setLookup} gyms={gyms} initialQ={q.trim()} onAdded={onAdded} />
    </div>
  );
}

function GhlLookupDialog({ open, onOpenChange, gyms, initialQ, onAdded }) {
  const connected = (gyms || []).filter((g) => g.connected);
  const [fid, setFid] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (open) { setQ(initialQ || ""); setRes(null); setFid((cur) => cur || connected[0]?.id || ""); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const find = async () => {
    setBusy(true);
    try {
      setRes(await nurtureGhlLookup(fid, q.trim()));
    } catch (e) {
      toast.error(err(e, "Couldn't search GoHighLevel"));
    } finally {
      setBusy(false);
    }
  };
  const add = async (c) => {
    setBusy(true);
    try {
      const r = await nurtureGhlAdd(fid, c.contact_id, c.opportunities?.[0]?.id);
      toast.success(r.added ? `${r.lead.name} added to the board` : `${r.lead.name}: ${r.note || "already on the board"}`);
      if (r.added && r.note) toast.warning(r.note);
      onOpenChange(false);
      onAdded?.(r.lead);
    } catch (e) {
      toast.error(err(e, "Couldn't add"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="ghl-lookup-dialog">
        <DialogHeader>
          <DialogTitle>Find a lead in GoHighLevel</DialogTitle>
          <DialogDescription>Type their phone or name. FPA looks them up in that gym&apos;s GoHighLevel and adds them to the board.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <select value={fid} onChange={(e) => setFid(e.target.value)} className="h-9 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm" data-testid="ghl-lookup-gym">
            {connected.map((g) => <option key={g.id} value={g.id}>{shortGym(g.name)}</option>)}
          </select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="98450 12345 or Priya" className="flex-1 min-w-[160px]"
            onKeyDown={(e) => { if (e.key === "Enter" && fid && q.trim().length >= 2) find(); }} data-testid="ghl-lookup-q" />
          <Button onClick={find} disabled={busy || !fid || q.trim().length < 2} data-testid="ghl-lookup-find">{busy ? "Searching…" : "Search"}</Button>
        </div>
        {res && (
          <div className="space-y-2">
            {res.contacts.length === 0 && <p className="text-sm text-zinc-400">Nobody matching in {shortGym(res.gym)}&apos;s GoHighLevel.</p>}
            {res.contacts.map((c) => {
              const o = c.opportunities?.[0];
              return (
                <div key={c.contact_id} className="rounded-md border border-[#262626] px-3 py-2 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-zinc-500">{c.phone || "no phone"}{c.email ? ` · ${c.email}` : ""}</div>
                      <div className="text-[11px] text-zinc-400">
                        {o ? `Lead pipeline: ${o.stage_name || "?"}${o.status && o.status !== "open" ? ` (${o.status})` : ""}` : "Not in the lead pipeline"}
                        {c.on_board ? ` · on FPA's board${c.on_board.status === "done" ? ` (done: ${c.on_board.done_reason || "closed"})` : ""}` : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" disabled={busy || !c.phone} onClick={() => add(c)} data-testid={`ghl-add-${c.contact_id}`}>
                      {c.on_board ? (c.on_board.status === "done" ? "Put back" : "Show") : "Add to board"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REASONS = {
  too_old: "Older than 7 days", closed_status: "Won / lost / abandoned", excluded_stage: "Showed / disqualified / sold…",
  scheduled_stage: "Scheduled / booked", four_week_not_due: "4 Week Follow Up, not due",
  no_phone: "No phone", other_pipeline: "Other pipeline",
};

// Managers: per gym, when FPA last read its GoHighLevel lead pipeline and why leads weren't taken.
export function IntakeStatusDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    getNurtureIntakeStatus().then(setData).catch((e) => { toast.error(err(e, "Couldn't load")); setData({ gyms: [] }); });
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-3xl max-h-[92dvh] overflow-y-auto" data-testid="intake-status">
        <DialogHeader>
          <DialogTitle>Lead intake from GoHighLevel</DialogTitle>
          <DialogDescription>
            Every minute FPA reads each gym&apos;s lead pipeline; on start-up and every {data?.full_scan_minutes || 30} min it reads every page back {data?.intake_days || 7} days.
            Open leads are taken unless their stage is scheduled / showed / disqualified / sold / lost (stages FPA doesn&apos;t recognise are taken and listed below).
            {data?.paused ? " Automation is paused (NURTURE_POLL_OFF): nothing is being read." : ""}
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-zinc-500">Loading…</div> : (
          <div className="space-y-3">
            {data.gyms.map((g) => {
              const s = g.last_full_scan || g.last_poll;
              return (
                <div key={g.facility_id} className="rounded-md border border-[#222] px-3 py-2 text-xs space-y-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-sm">{shortGym(g.gym)}</span>
                    <span className="text-zinc-500">
                      {!g.connected ? "Not connected" : g.last_poll_at ? `Last read ${when(g.last_poll_at)}${g.last_full_scan_at ? ` · full scan ${when(g.last_full_scan_at)}` : ""}` : "Not read yet"}
                    </span>
                  </div>
                  {g.last_poll?.error && <div className="text-rose-300">Error: {g.last_poll.error}</div>}
                  {g.ghl_bot_warning && <div className="text-rose-300" data-testid={`ghl-bot-${g.facility_id}`}>{g.ghl_bot_warning}</div>}
                  {s && (
                    <>
                      <div className="text-zinc-300">
                        Last full scan: {s.seen} seen · <b>{s.added} added</b> · {s.existing} already on the board{s.left_board ? ` · ${s.left_board} left the board` : ""} · {s.pages} page{s.pages === 1 ? "" : "s"}
                      </div>
                      {Object.keys(s.skipped || {}).length > 0 && (
                        <div className="text-zinc-400">
                          Skipped: {Object.entries(s.skipped).map(([k, n]) => `${REASONS[k] || k} ${n}`).join(" · ")}
                        </div>
                      )}
                      {Object.keys(s.unknown_stages || {}).length > 0 && (
                        <div className="text-amber-300/90">
                          Unknown stages (taken as open; exclude them in settings if they shouldn&apos;t be): {Object.entries(s.unknown_stages).map(([k, n]) => `${k} (${n})`).join(", ")}
                        </div>
                      )}
                      {(s.samples || []).filter((x) => x.reason !== "too_old").length > 0 && (
                        <details>
                          <summary className="cursor-pointer text-zinc-500">Examples</summary>
                          <ul className="mt-1 space-y-0.5 text-zinc-400">
                            {s.samples.map((x) => <li key={`${x.opportunity_id}-${x.reason}`}>{x.name || "—"} · {x.stage || "?"} · {REASONS[x.reason] || x.reason}</li>)}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

